// ============================================================================
// EscalateDropdown — single danger button + portal menu (shared component)
// ============================================================================
// Domain-agnostic dropdown for Escalate-style branching choices. Used by
// Pre-Litigation (Arbitration / SARFAESI) and Phase 9 Litigation Case Shell
// (Stage-11 multi-target picker: Arbitration / NI-Act / Civil), as well as
// any future workflow that branches at a state to one of N targets.
//
// Portal rendering matches the project-wide pattern (ActionMenu.tsx) so the
// menu escapes any positioned ancestor (including the XL modal container).
//
// Usage (Pre-Litigation):
//   <EscalateDropdown
//     options={[
//       { target: 'ARBITRATION', label: 'Arbitration',
//         description: 'Refer to arbitrator (unsecured / general)' },
//       { target: 'SARFAESI', label: 'SARFAESI',
//         description: kase.isSecured ? 'Enforce security under SARFAESI Act' : 'Requires a secured asset',
//         disabled: !kase.isSecured,
//         disabledReason: 'SARFAESI requires a secured asset' },
//     ]}
//     onSelect={(target) => setEscalateTarget(target)}
//   />
//
// Customise the trigger label + heading via the optional triggerLabel + headingLabel
// props (defaults: "Escalate" / "Escalate to"). Generic over the target type so
// each consumer constrains it to their domain enum.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';

export interface EscalateOption<T extends string = string> {
  target: T;
  label: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface EscalateDropdownProps<T extends string = string> {
  options: EscalateOption<T>[];
  onSelect: (target: T) => void;
  disabled?: boolean;
  /** Trigger button label. Defaults to "Escalate". */
  triggerLabel?: string;
  /** Dropdown heading. Defaults to "Escalate to". */
  headingLabel?: string;
}

function EscalateDropdown<T extends string = string>({
  options,
  onSelect,
  disabled,
  triggerLabel = 'Escalate',
  headingLabel = 'Escalate to',
}: EscalateDropdownProps<T>): React.ReactElement {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; minWidth: number }>({ top: 0, left: 0, minWidth: 240 });

  const calculatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 280),
      minWidth: Math.max(rect.width, 260),
    });
  }, []);

  // Phase 1: open below
  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!open) calculatePosition();
    setOpen(v => !v);
  }, [open, disabled, calculatePosition]);

  // Phase 2: flip up if overflowing the viewport
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !triggerRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    if (menuRect.bottom > vh - 8) {
      setPos(p => ({ ...p, top: triggerRect.top - menuRect.height - 4 }));
    }
  }, [open]);

  // Close on scroll, resize, outside click
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const handlePick = (opt: EscalateOption<T>) => {
    if (opt.disabled) return;
    setOpen(false);
    onSelect(opt.target);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        title={disabled ? undefined : `Choose a${headingLabel.startsWith('Choose') ? '' : 'n'} option`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.4rem 0.85rem', fontSize: '0.8rem', fontWeight: 500,
          border: '1px solid #dc2626', borderRadius: '6px',
          background: '#fee2e2', color: '#991b1b',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <IconSystem.arrowRight size={14} />
        {triggerLabel}
        <IconSystem.chevronDown size={12} />
      </button>

      {open && createPortal(
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          />
          <div
            ref={menuRef}
            className="action-menu-dropdown"
            role="menu"
            style={{
              position: 'fixed',
              top: pos.top, left: pos.left,
              zIndex: 9999,
              minWidth: pos.minWidth,
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.18), 0 4px 10px -6px rgba(0,0,0,0.12)',
              padding: '0.35rem',
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '0.4rem 0.6rem 0.3rem',
              fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {headingLabel}
            </div>
            {options.map(opt => (
              <button
                key={opt.target}
                role="menuitem"
                type="button"
                onClick={() => handlePick(opt)}
                disabled={opt.disabled}
                title={opt.disabled ? opt.disabledReason : undefined}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  width: '100%', padding: '0.5rem 0.7rem', gap: '0.15rem',
                  border: 'none', background: 'transparent',
                  textAlign: 'left', cursor: opt.disabled ? 'not-allowed' : 'pointer',
                  borderRadius: '6px', opacity: opt.disabled ? 0.55 : 1,
                  color: 'var(--text-primary)',
                }}
                onMouseEnter={e => { if (!opt.disabled) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: opt.disabled ? 'var(--text-secondary)' : '#b91c1c' }}>
                  <IconSystem.arrowRight size={13} />
                  {opt.label}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: 1.3 }}>
                  {opt.description}
                </div>
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export default EscalateDropdown;
